import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2DataGrid,
  V2EmptyState,
  V2FormField,
  V2FormGrid,
  LoadingState,
  StatePanel,
  V2CloseButton,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type { FiscalPeriodListResponse, FiscalYearListResponse } from '../../lib/types/finance.js';

interface CurrencyItem {
  readonly currencyId: string;
  readonly code: string;
  readonly name: string;
  readonly symbol: string | null;
  readonly decimalPlaces: number;
  readonly isBase: boolean;
  readonly status: string;
}

interface ExchangeRateItem {
  readonly rateId: string;
  readonly fromCurrencyCode: string;
  readonly toCurrencyCode: string;
  readonly rateDate: string;
  readonly rateType: string;
  readonly rateRial: string;
  readonly source: string;
  readonly evidenceRef: string | null;
  readonly createdAt: string;
}

interface FxRevaluationItem {
  readonly revaluationId: string;
  readonly fiscalYearId: string;
  readonly fiscalPeriodId: string;
  readonly currencyCode: string;
  readonly revaluationDate: string;
  readonly rateId: string;
  readonly exchangeRateRial: string;
  readonly totalForeignBalance: string;
  readonly bookBalanceRial: string;
  readonly revaluedBalanceRial: string;
  readonly unrealizedGainLossRial: string;
  readonly journalId: string | null;
  readonly status: string;
}

export function CurrenciesPage() {
  const { unit, label } = useAmountDisplay();
  const { slug } = useParams<{ slug: string }>();
  const base = tenantBasePath(slug ?? '');

  const [activeTab, setActiveTab] = useState<'currencies' | 'rates' | 'revaluations'>('currencies');
  const [refreshKey, setRefreshKey] = useState(0);

  // Modals
  const [showAddCurrencyModal, setShowAddCurrencyModal] = useState(false);
  const [showAddRateModal, setShowAddRateModal] = useState(false);
  const [showRevalModal, setShowRevalModal] = useState(false);

  // Currency form
  const [currencyCode, setCurrencyCode] = useState('');
  const [currencyName, setCurrencyName] = useState('');
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [decimalPlaces, setDecimalPlaces] = useState(2);

  // Rate form
  const [rateFromCurrency, setRateFromCurrency] = useState('USD');
  const [rateDate, setRateDate] = useState('');
  const [rateType, setRateType] = useState('commercial');
  const [rateRial, setRateRial] = useState('');
  const [rateSource, setRateSource] = useState('بانک مرکزی / بازار متشکل');
  const [evidenceRef, setEvidenceRef] = useState('');

  // Revaluation form
  const [revalFiscalYearId, setRevalFiscalYearId] = useState('');
  const [revalFiscalPeriodId, setRevalFiscalPeriodId] = useState('');
  const [revalCurrencyCode, setRevalCurrencyCode] = useState('USD');
  const [revalDate, setRevalDate] = useState('');

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const currencies = useApiGet<{ items: CurrencyItem[] }>(
    slug ? `${base}/api/v1/finance/currency/currencies` : null,
    [slug, refreshKey],
  );

  const exchangeRates = useApiGet<{ items: ExchangeRateItem[] }>(
    slug ? `${base}/api/v1/finance/currency/exchange-rates` : null,
    [slug, refreshKey],
  );

  const revaluations = useApiGet<{ items: FxRevaluationItem[] }>(
    slug ? `${base}/api/v1/finance/currency/fx-revaluation-runs` : null,
    [slug, refreshKey],
  );

  const years = useApiGet<FiscalYearListResponse>(
    slug ? `${base}/api/v1/finance/fiscal-years` : null,
    [slug],
  );

  const periods = useApiGet<FiscalPeriodListResponse>(
    slug ? `${base}/api/v1/accounting/fiscal-periods` : null,
    [slug],
  );

  if (!slug) return null;

  async function handleAddCurrency(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      await apiPost(`${base}/api/v1/finance/currency/currencies`, {
        code: currencyCode,
        name: currencyName,
        symbol: currencySymbol || null,
        decimalPlaces,
      });
      setShowAddCurrencyModal(false);
      setRefreshKey((k) => k + 1);
      setCurrencyCode('');
      setCurrencyName('');
      setCurrencySymbol('');
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'خطا در ثبت واحد پول');
    } finally {
      setBusy(false);
    }
  }

  async function handleAddRate(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      await apiPost(`${base}/api/v1/finance/currency/exchange-rates`, {
        fromCurrencyCode: rateFromCurrency,
        rateDate,
        rateType,
        rateRial,
        source: rateSource,
        evidenceRef: evidenceRef || null,
      });
      setShowAddRateModal(false);
      setRefreshKey((k) => k + 1);
      setRateRial('');
      setEvidenceRef('');
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'خطا در ثبت نرخ برابری');
    } finally {
      setBusy(false);
    }
  }

  async function handleRunRevaluation(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      await apiPost(`${base}/api/v1/finance/currency/fx-revaluation-runs`, {
        fiscalYearId: revalFiscalYearId,
        fiscalPeriodId: revalFiscalPeriodId,
        currencyCode: revalCurrencyCode,
        revaluationDate: revalDate,
      });
      setShowRevalModal(false);
      setRefreshKey((k) => k + 1);
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'خطا در اجرای تسعیر ارز');
    } finally {
      setBusy(false);
    }
  }

  const rateTypeLabels: Record<string, string> = {
    commercial: 'تجاری / توافقی',
    official: 'رسمی (بانک مرکزی)',
    nima: 'سامانه نیما',
    free: 'آزاد',
  };

  return (
    <div className="sk-page-container">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 700, margin: 0 }}>
            چندارزی و تسعیر پایان دوره
          </h1>
          <p
            style={{
              color: 'var(--sk-text-muted, #666)',
              margin: '4px 0 0 0',
              fontSize: '0.875rem',
            }}
          >
            مدیریت ارزها، ثبت نرخ‌های برابری تسعیر و اجرای تسعیر مانده‌های باز (FIN-ADV-06)
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {activeTab === 'currencies' && (
            <V2Button variant="primary" onClick={() => setShowAddCurrencyModal(true)}>
              + تعریف واحد پول جدید
            </V2Button>
          )}
          {activeTab === 'rates' && (
            <V2Button variant="primary" onClick={() => setShowAddRateModal(true)}>
              + ثبت نرخ برابری جدید
            </V2Button>
          )}
          {activeTab === 'revaluations' && (
            <V2Button variant="primary" onClick={() => setShowRevalModal(true)}>
              + اجرای تسعیر ارز پایان دوره
            </V2Button>
          )}
        </div>
      </div>

      {actionError && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: 'var(--sk-danger-soft, #fee2e2)',
            borderRadius: '8px',
            color: 'var(--sk-danger, #ef4444)',
            fontSize: '13px',
            marginBottom: 16,
          }}
        >
          {actionError}
        </div>
      )}

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          borderBottom: '1px solid var(--sk-border-color, #e0e0e0)',
          marginBottom: 20,
        }}
      >
        <button
          type="button"
          onClick={() => setActiveTab('currencies')}
          style={{
            padding: '8px 16px',
            border: 'none',
            background: 'none',
            borderBottom:
              activeTab === 'currencies' ? '2px solid var(--sk-color-primary, #0284c7)' : 'none',
            fontWeight: activeTab === 'currencies' ? 700 : 400,
            cursor: 'pointer',
          }}
        >
          واحدهای پول
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('rates')}
          style={{
            padding: '8px 16px',
            border: 'none',
            background: 'none',
            borderBottom:
              activeTab === 'rates' ? '2px solid var(--sk-color-primary, #0284c7)' : 'none',
            fontWeight: activeTab === 'rates' ? 700 : 400,
            cursor: 'pointer',
          }}
        >
          نرخ‌های برابری (تغییرناپذیر)
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('revaluations')}
          style={{
            padding: '8px 16px',
            border: 'none',
            background: 'none',
            borderBottom:
              activeTab === 'revaluations' ? '2px solid var(--sk-color-primary, #0284c7)' : 'none',
            fontWeight: activeTab === 'revaluations' ? 700 : 400,
            cursor: 'pointer',
          }}
        >
          تسعیر ارز پایان دوره
        </button>
      </div>

      {/* Tab 1: Currencies Master */}
      {activeTab === 'currencies' && (
        <V2Card>
          <V2CardHeader title="واحدهای پول تعریف‌شده" />
          {currencies.status === 'loading' && <LoadingState />}
          {currencies.status === 'error' && (
            <StatePanel variant="network_error" title="خطا در دریافت ارزها" />
          )}
          {currencies.status === 'ready' && currencies.data.items.length === 0 && (
            <V2EmptyState
              title="ارزی تعریف نشده است"
              description="برای شروع، ارز جدید اضافه کنید."
            />
          )}
          {currencies.status === 'ready' && currencies.data.items.length > 0 && (
            <V2DataGrid
              columns={[
                {
                  key: 'code',
                  header: 'کد ارز',
                  render: (row: CurrencyItem) => (
                    <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{row.code}</span>
                  ),
                },
                { key: 'name', header: 'نام ارز', render: (row: CurrencyItem) => row.name },
                { key: 'symbol', header: 'نماد', render: (row: CurrencyItem) => row.symbol ?? '—' },
                {
                  key: 'decimals',
                  header: 'اعشار',
                  render: (row: CurrencyItem) => row.decimalPlaces,
                },
                {
                  key: 'isBase',
                  header: 'ارز پایه',
                  render: (row: CurrencyItem) =>
                    row.isBase ? (
                      <V2Badge tone="success">ارز پایه سازمان (غیرقابل تغییر)</V2Badge>
                    ) : (
                      <V2Badge tone="neutral">ارز فرعی</V2Badge>
                    ),
                },
                {
                  key: 'status',
                  header: 'وضعیت',
                  render: (row: CurrencyItem) => (
                    <V2Badge tone={row.status === 'active' ? 'success' : 'neutral'}>
                      {row.status === 'active' ? 'فعال' : 'غیرفعال'}
                    </V2Badge>
                  ),
                },
              ]}
              rows={currencies.data.items}
              rowKey={(row: CurrencyItem) => row.currencyId}
            />
          )}
        </V2Card>
      )}

      {/* Tab 2: Exchange Rates */}
      {activeTab === 'rates' && (
        <V2Card>
          <V2CardHeader
            title="تاریخچه نرخ‌های برابری (سوابق قطعی و تغییرناپذیر)"
            subtitle="هر نرخ با منبع و زمان معتبر به صورت تغییرناپذیر ثبت می‌شود"
          />
          {exchangeRates.status === 'loading' && <LoadingState />}
          {exchangeRates.status === 'error' && (
            <StatePanel variant="network_error" title="خطا در دریافت نرخ‌ها" />
          )}
          {exchangeRates.status === 'ready' && exchangeRates.data.items.length === 0 && (
            <V2EmptyState
              title="نرخی ثبت نشده است"
              description="نرخ‌های برابری روزانه یا تسعیر را ثبت کنید."
            />
          )}
          {exchangeRates.status === 'ready' && exchangeRates.data.items.length > 0 && (
            <V2DataGrid
              columns={[
                {
                  key: 'pair',
                  header: 'جفت ارز',
                  render: (row: ExchangeRateItem) => (
                    <span style={{ fontWeight: 600 }}>
                      {row.fromCurrencyCode} / {row.toCurrencyCode}
                    </span>
                  ),
                },
                {
                  key: 'date',
                  header: 'تاریخ نرخ',
                  render: (row: ExchangeRateItem) => displayJalaliDate(row.rateDate),
                },
                {
                  key: 'type',
                  header: 'نوع نرخ',
                  render: (row: ExchangeRateItem) => (
                    <V2Badge tone="neutral">{rateTypeLabels[row.rateType] ?? row.rateType}</V2Badge>
                  ),
                },
                {
                  key: 'rate',
                  header: `نرخ برابری (${label})`,
                  render: (row: ExchangeRateItem) => (
                    <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>
                      {formatRialFa(row.rateRial, unit)}
                    </span>
                  ),
                },
                {
                  key: 'source',
                  header: 'منبع استعلام',
                  render: (row: ExchangeRateItem) => row.source,
                },
                {
                  key: 'evidence',
                  header: 'مستند / ارجاع',
                  render: (row: ExchangeRateItem) => row.evidenceRef ?? '—',
                },
              ]}
              rows={exchangeRates.data.items}
              rowKey={(row: ExchangeRateItem) => row.rateId}
            />
          )}
        </V2Card>
      )}

      {/* Tab 3: Revaluations */}
      {activeTab === 'revaluations' && (
        <V2Card>
          <V2CardHeader
            title="عملیات تسعیر پایان دوره"
            subtitle="ایجاد خودکار سند پیش‌نویس تعدیل سود و زیان تحقق‌نیافته بدون صدور قطعی"
          />
          {revaluations.status === 'loading' && <LoadingState />}
          {revaluations.status === 'error' && (
            <StatePanel variant="network_error" title="خطا در دریافت تسعیرها" />
          )}
          {revaluations.status === 'ready' && revaluations.data.items.length === 0 && (
            <V2EmptyState
              title="تسعیری انجام نشده است"
              description="در پایان هر دوره مالی، تسعیر مانده‌های ارزی را اجرا نمایید."
            />
          )}
          {revaluations.status === 'ready' && revaluations.data.items.length > 0 && (
            <V2DataGrid
              columns={[
                {
                  key: 'currency',
                  header: 'ارز',
                  render: (row: FxRevaluationItem) => (
                    <span style={{ fontWeight: 600 }}>{row.currencyCode}</span>
                  ),
                },
                {
                  key: 'date',
                  header: 'تاریخ تسعیر',
                  render: (row: FxRevaluationItem) => displayJalaliDate(row.revaluationDate),
                },
                {
                  key: 'rate',
                  header: `نرخ تسعیر (${label})`,
                  render: (row: FxRevaluationItem) => formatRialFa(row.exchangeRateRial, unit),
                },
                {
                  key: 'foreignBalance',
                  header: 'مانده ارزی باز',
                  render: (row: FxRevaluationItem) => (
                    <span style={{ fontFamily: 'monospace' }}>{row.totalForeignBalance}</span>
                  ),
                },
                {
                  key: 'bookBalance',
                  header: `مانده دفتری قبلی (${label})`,
                  render: (row: FxRevaluationItem) => formatRialFa(row.bookBalanceRial, unit),
                },
                {
                  key: 'revaluedBalance',
                  header: `ارزش تسعیر شده (${label})`,
                  render: (row: FxRevaluationItem) => formatRialFa(row.revaluedBalanceRial, unit),
                },
                {
                  key: 'gainLoss',
                  header: `سود / (زیان) تسعیر (${label})`,
                  render: (row: FxRevaluationItem) => {
                    const isGain = BigInt(row.unrealizedGainLossRial) >= 0n;
                    return (
                      <V2Badge tone={isGain ? 'success' : 'danger'}>
                        {formatRialFa(row.unrealizedGainLossRial, unit)}
                      </V2Badge>
                    );
                  },
                },
                {
                  key: 'journal',
                  header: 'سند پیش‌نویس',
                  render: (row: FxRevaluationItem) =>
                    row.journalId ? (
                      <Link
                        to={`${base}/journal/${row.journalId}`}
                        style={{
                          color: 'var(--sk-color-primary, #0284c7)',
                          textDecoration: 'underline',
                        }}
                      >
                        مشاهده سند
                      </Link>
                    ) : (
                      '—'
                    ),
                },
                {
                  key: 'status',
                  header: 'وضعیت',
                  render: (row: FxRevaluationItem) => (
                    <V2Badge tone={row.status === 'draft' ? 'warn' : 'success'}>
                      {row.status === 'draft' ? 'پیش‌نویس' : row.status}
                    </V2Badge>
                  ),
                },
              ]}
              rows={revaluations.data.items}
              rowKey={(row: FxRevaluationItem) => row.revaluationId}
            />
          )}
        </V2Card>
      )}

      {/* Modal: Add Currency */}
      {showAddCurrencyModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--sk-bg-card, #fff)',
              borderRadius: '12px',
              padding: '24px',
              width: '450px',
              maxWidth: '90vw',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}
            >
              <V2CloseButton onClick={() => setShowAddCurrencyModal(false)} />
            </div>
            <h2 style={{ fontSize: '1.2rem', fontWeight: 700, margin: '0 0 16px 0' }}>
              تعریف واحد پول جدید
            </h2>
            <form onSubmit={handleAddCurrency}>
              <V2FormGrid>
                <V2FormField label="کد استاندارد (مانند USD, EUR)" required width="full">
                  <input
                    className="sk-input ltr"
                    value={currencyCode}
                    onChange={(e) => setCurrencyCode(e.target.value.toUpperCase())}
                    required
                    maxLength={5}
                  />
                </V2FormField>
                <V2FormField label="نام واحد پول (مانند دلار آمریکا)" required width="full">
                  <input
                    className="sk-input"
                    value={currencyName}
                    onChange={(e) => setCurrencyName(e.target.value)}
                    required
                  />
                </V2FormField>
                <V2FormField label="نماد ارز (اختیاری)" width="full">
                  <input
                    className="sk-input"
                    value={currencySymbol}
                    onChange={(e) => setCurrencySymbol(e.target.value)}
                  />
                </V2FormField>
                <V2FormField label="تعداد اعشار" width="full">
                  <input
                    type="number"
                    className="sk-input ltr"
                    value={decimalPlaces}
                    onChange={(e) => setDecimalPlaces(Number(e.target.value))}
                    min={0}
                    max={6}
                  />
                </V2FormField>
              </V2FormGrid>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <V2Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowAddCurrencyModal(false)}
                >
                  انصراف
                </V2Button>
                <V2Button type="submit" variant="primary" disabled={busy}>
                  ثبت واحد پول
                </V2Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Add Rate */}
      {showAddRateModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--sk-bg-card, #fff)',
              borderRadius: '12px',
              padding: '24px',
              width: '450px',
              maxWidth: '90vw',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}
            >
              <V2CloseButton onClick={() => setShowAddRateModal(false)} />
            </div>
            <h2 style={{ fontSize: '1.2rem', fontWeight: 700, margin: '0 0 16px 0' }}>
              ثبت نرخ برابری ارز
            </h2>
            <form onSubmit={handleAddRate}>
              <V2FormGrid>
                <V2FormField label="ارز مبدا" required width="full">
                  <select
                    className="sk-select"
                    value={rateFromCurrency}
                    onChange={(e) => setRateFromCurrency(e.target.value)}
                    required
                  >
                    {currencies.status === 'ready' &&
                      currencies.data.items
                        .filter((c) => !c.isBase)
                        .map((c) => (
                          <option key={c.currencyId} value={c.code}>
                            {c.code} — {c.name}
                          </option>
                        ))}
                  </select>
                </V2FormField>
                <V2FormField label="تاریخ نرخ" required width="full">
                  <JalaliDateInput value={rateDate} onChange={setRateDate} />
                </V2FormField>
                <V2FormField label="نوع نرخ" required width="full">
                  <select
                    className="sk-select"
                    value={rateType}
                    onChange={(e) => setRateType(e.target.value)}
                    required
                  >
                    <option value="commercial">تجاری / توافقی</option>
                    <option value="official">رسمی (بانک مرکزی)</option>
                    <option value="nima">سامانه نیما</option>
                    <option value="free">آزاد</option>
                  </select>
                </V2FormField>
                <V2FormField label={`نرخ برابری (ریال ایران به ازای ۱ واحد)`} required width="full">
                  <input
                    className="sk-input ltr sk-num"
                    value={rateRial}
                    onChange={(e) => setRateRial(e.target.value)}
                    required
                    placeholder="مثال: 650000"
                  />
                </V2FormField>
                <V2FormField label="منبع استعلام نرخ" required width="full">
                  <input
                    className="sk-input"
                    value={rateSource}
                    onChange={(e) => setRateSource(e.target.value)}
                    required
                  />
                </V2FormField>
                <V2FormField label="شماره بخشنامه یا شناسه استعلام (اختیاری)" width="full">
                  <input
                    className="sk-input"
                    value={evidenceRef}
                    onChange={(e) => setEvidenceRef(e.target.value)}
                  />
                </V2FormField>
              </V2FormGrid>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <V2Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowAddRateModal(false)}
                >
                  انصراف
                </V2Button>
                <V2Button type="submit" variant="primary" disabled={busy}>
                  ثبت نرخ قطعی
                </V2Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Run Revaluation */}
      {showRevalModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--sk-bg-card, #fff)',
              borderRadius: '12px',
              padding: '24px',
              width: '450px',
              maxWidth: '90vw',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}
            >
              <V2CloseButton onClick={() => setShowRevalModal(false)} />
            </div>
            <h2 style={{ fontSize: '1.2rem', fontWeight: 700, margin: '0 0 16px 0' }}>
              محاسبهٔ تسعیر ارز پایان دوره
            </h2>
            <form onSubmit={handleRunRevaluation}>
              <V2FormGrid>
                <V2FormField label="سال مالی" required width="full">
                  <select
                    className="sk-select"
                    value={revalFiscalYearId}
                    onChange={(e) => setRevalFiscalYearId(e.target.value)}
                    required
                  >
                    <option value="">انتخاب سال مالی</option>
                    {years.status === 'ready' &&
                      years.data.items.map((y) => (
                        <option key={y.fiscalYearId} value={y.fiscalYearId}>
                          {y.name}
                        </option>
                      ))}
                  </select>
                </V2FormField>
                <V2FormField label="دوره مالی" required width="full">
                  <select
                    className="sk-select"
                    value={revalFiscalPeriodId}
                    onChange={(e) => setRevalFiscalPeriodId(e.target.value)}
                    required
                  >
                    <option value="">انتخاب دوره مالی</option>
                    {periods.status === 'ready' &&
                      periods.data.items
                        .filter((p) => !revalFiscalYearId || p.fiscalYearId === revalFiscalYearId)
                        .map((p) => (
                          <option key={p.fiscalPeriodId} value={p.fiscalPeriodId}>
                            {p.name}
                          </option>
                        ))}
                  </select>
                </V2FormField>
                <V2FormField label="واحد پول برای تسعیر" required width="full">
                  <select
                    className="sk-select"
                    value={revalCurrencyCode}
                    onChange={(e) => setRevalCurrencyCode(e.target.value)}
                    required
                  >
                    {currencies.status === 'ready' &&
                      currencies.data.items
                        .filter((c) => !c.isBase)
                        .map((c) => (
                          <option key={c.currencyId} value={c.code}>
                            {c.code} — {c.name}
                          </option>
                        ))}
                  </select>
                </V2FormField>
                <V2FormField label="تاریخ اعمال تسعیر" required width="full">
                  <JalaliDateInput value={revalDate} onChange={setRevalDate} />
                </V2FormField>
              </V2FormGrid>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
                <V2Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowRevalModal(false)}
                >
                  انصراف
                </V2Button>
                <V2Button type="submit" variant="primary" disabled={busy}>
                  محاسبه و صدور پیش‌نویس
                </V2Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
